// Shared business logic for the Home v2 screen (mobile-web build).
//
// Both the desktop and the mobile layouts of `DashboardScreen.web.tsx` consume
// this hook so they call the SAME controllers, dispatch the SAME actions and
// stay in sync across window resizes. Rendering files are presentation-only.
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Animated } from 'react-native'

import { isSmartAccount } from '@ambire-common/libs/account/account'
import { SelectedAccountPortfolioTokenResult } from '@ambire-common/interfaces/selectedAccount'
import { AssetType } from '@ambire-common/libs/defiPositions/types'
import { getTokenAmount, getTokenBalanceInUSD } from '@ambire-common/libs/portfolio/helpers'
import { TokenResult } from '@ambire-common/libs/portfolio/interfaces'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import shortenAddress from '@ambire-common/utils/shortenAddress'
import useNavigation from '@common/hooks/useNavigation'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'
import { storage } from '@common/services/storage'
import { tokenOrCollectionSearch } from '@common/utils/search'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import getAndFormatTokenDetails from '@common/modules/dashboard/helpers/getTokenDetails'
import { useDashboardPrivatePendingRow } from '@common/modules/dashboard/hooks/useDashboardPrivatePendingRow'
import { useRailgunOverviewBalance } from '@common/modules/dashboard/hooks/useRailgunOverviewBalance'
import { useRailgunShieldPending } from '@common/modules/dashboard/hooks/useRailgunShieldPending'
import {
  resolveDashboardNetworkFilterForModeChange,
  resolveExtensionPrivateInvariantFilter
} from '@common/modules/dashboard/utils/dashboardModeNetworkFilter'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { isExtension } from '@web/constants/browserapi'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import { useRailgunMerkletreeScanContext } from '@web/contexts/railgunContext'
import useActivityControllerState from '@web/hooks/useActivityControllerState'
import useAppMode from '@web/hooks/useAppMode'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import usePortfolioControllerState from '@web/hooks/usePortfolioControllerState'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useRailgunTokens from '@web/hooks/useRailgunTokens/useRailgunTokens'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { computePrivateModeReason, isPrivateModeBlocked } from '@web/utils/privateModeAvailability'
import { getBalanceUsdByChain, getMissingPOIUsdByToken, priceKey } from '@web/utils/railgunTokens'
import { getTokenId } from '@web/utils/token'

import { accountOpToActivityItem } from './recentActivity'

// Recent-activity panel: how many ops to fetch and how many to display.
const RECENT_ACTIVITY_FETCH = 10
const RECENT_ACTIVITY_SHOW = 4
const RECENT_ACTIVITY_WINDOW_MS = 24 * 60 * 60 * 1000

export type AssetRowData = {
  id: string
  symbol: string
  network: string
  chainId: bigint
  address: string
  decimals: number
  balanceFormatted: string
  balanceUSDFormatted: string
  priceUSDFormatted: string
  /** 24h price change percent (signed), or null when unknown. */
  deltaPct: number | null
  /** Private mode only: USD value of this token's funds still awaiting POI
   * verification (the per-token slice of the "Incoming $X" pill). 0 otherwise. */
  pendingPOIUsd: number
  raw: SelectedAccountPortfolioTokenResult
}

const hasAmount = (token: SelectedAccountPortfolioTokenResult) =>
  token.amount > 0n || (token.amountPostSimulation != null && token.amountPostSimulation > 0n)

const get24hChange = (token: SelectedAccountPortfolioTokenResult): number | null => {
  // `marketDataIn` (like `priceIn`) can arrive as a non-array on the mobile-web
  // worker→page path (iOS Safari); guard so `.find` can't white-screen the dashboard.
  const marketDataIn = Array.isArray(token.marketDataIn) ? token.marketDataIn : []
  const usd = marketDataIn.find((m) => m.baseCurrency?.toLowerCase() === 'usd')
  return typeof usd?.usd_24h_change === 'number' ? usd.usd_24h_change : null
}

const useHomeDashboard = () => {
  const { mode, setMode, railgunEngineInitializing, railgunEngineInitializingError, ensureRailgunEngine } =
    useAppMode()
  const { navigate } = useNavigation()
  const { dispatch } = useBackgroundService()
  const { account, portfolio, dashboardNetworkFilter } = useSelectedAccountControllerState()
  // PortfolioController.tokenPreferences is updated + emitted synchronously by
  // `toggleHideToken` (before the network re-fetch that actually stamps
  // token.flags.isHidden). The dashboard row otherwise stays visible until that
  // re-fetch lands — and on a slow/erroring chain (e.g. PulseChain) it may never
  // land, leaving a "hidden" token on screen. Filter against the preference
  // directly so the row disappears the instant the user confirms Hide, and
  // reappears the instant they un-hide from Settings.
  const portfolioCtrl = usePortfolioControllerState()
  const tokenPreferences = (portfolioCtrl?.tokenPreferences || []) as {
    address: string
    chainId: bigint
    isHidden?: boolean
  }[]
  // User-added (custom) tokens. These must stay on the dashboard even at a 0
  // balance — the only way to remove one is the explicit "Hide token" action.
  const customTokens = (portfolioCtrl?.customTokens || []) as {
    address: string
    chainId: bigint
  }[]
  const { networks } = useExtensionAwareNetworks()
  const {
    currentRailgunWallet,
    allRailgunWallets,
    isLoading: railgunIsLoading,
    isBalanceFetchingError
  } = useRailgunContext()
  const { keys } = useKeystoreControllerState()
  const { accountsOps } = useActivityControllerState()
  const railgunTokens = useRailgunTokens()
  const railgunOverview = useRailgunOverviewBalance(mode)
  const { scanningProgress } = useRailgunMerkletreeScanContext()

  const { networks: allNetworks } = useNetworksControllerState()

  const [hidden, setHidden] = useState(false)
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null)
  // "Hide token" confirmation popup + its "Don't ask me again" opt-out (persisted).
  const [hideTokenModalVisible, setHideTokenModalVisible] = useState(false)
  const [doNotAskHideToken, setDoNotAskHideToken] = useState(false)
  // Asset-type tab (tokens / collectibles / defi) — same model as the previous
  // dashboard. Drives the toolbar "asset types" dropdown.
  const [openTab, setOpenTab] = useState<TabType>('tokens')
  const [initTab, setInitTab] = useState<{ [key: string]: boolean }>({ tokens: true })
  const [sessionId] = useState(() => `home-${nanoid()}`)
  // Stable animated value to satisfy the existing list components' API. Home
  // doesn't run the collapse animation, so it stays at 0.
  const animatedOverviewHeight = useRef(new Animated.Value(0)).current

  // Search — same control wired into the real Search input; filters tokens.
  const { control: searchControl, watch, setValue } = useForm<{ search: string }>({
    mode: 'all',
    defaultValues: { search: '' }
  })
  const searchValue = watch('search')

  const isPrivate = mode === ModeEnum.PRIVATE

  // Lazy-init a tab the first time it's opened (mirrors DashboardPages).
  useEffect(() => {
    if (!initTab[openTab]) setInitTab((prev) => ({ ...prev, [openTab]: true }))
  }, [openTab, initTab])

  // Register the home session so Activity sessions terminate on unmount. The
  // sessionId is kept in local state only (not mirrored into the URL) — on
  // mobile-web cleanup runs from the effect-return dispatch below, so the URL
  // doesn't need to carry it for the extension's port-disconnect handler.
  useEffect(() => {
    return () => {
      dispatch({ type: 'MAIN_CONTROLLER_ACTIVITY_RESET_ACC_OPS_FILTERS', params: { sessionId } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId])

  // Reset to the tokens tab when the wallet mode changes.
  useEffect(() => {
    setOpenTab('tokens')
  }, [mode])

  // Recent-activity panel (public only) — wire the REAL ActivityController under
  // the home session, same as the full Activity screen. Without this the panel
  // never receives ops and is stuck on its empty state. Cleanup is handled by
  // the ACTIVITY_RESET dispatch in the session-registration effect above.
  const dispatchRecentFilters = useCallback(() => {
    if (!account?.addr) return
    dispatch({
      type: 'MAIN_CONTROLLER_ACTIVITY_SET_ACC_OPS_FILTERS',
      params: {
        sessionId,
        filters: { account: account.addr },
        pagination: { itemsPerPage: RECENT_ACTIVITY_FETCH, fromPage: 0 }
      }
    })
  }, [account?.addr, dispatch, sessionId])

  useEffect(() => {
    if (isPrivate) return
    dispatchRecentFilters()
  }, [isPrivate, dispatchRecentFilters])

  // Self-heal a session left on the PREVIOUS account: the ActivityController's
  // background `syncFilteredAccountsOps` re-applies each session's stored filters
  // and can race/clobber our account-switch dispatch back to the old account
  // (last async write wins; mobile-web's storage round-trip widens the window).
  // Re-fire (debounced) until the stored session matches the current account, or
  // the recent-activity panel stays stuck on the previous account's ops. Mirrors
  // the same guard in useActivityScreen.
  const recentSession = accountsOps?.[sessionId]
  const recentSessionStale =
    !isPrivate && !!account?.addr && !!recentSession && recentSession.filters.account !== account.addr
  useEffect(() => {
    if (!recentSessionStale) return
    const id = setTimeout(dispatchRecentFilters, 400)
    return () => clearTimeout(id)
  }, [recentSessionStale, dispatchRecentFilters])

  const recentActivity = useMemo(() => {
    if (isPrivate) return []
    const session = accountsOps?.[sessionId]
    // Stable sessionId: after an account switch this entry still holds the
    // previous account's ops until the re-dispatched filter round-trips. Only
    // trust the result once its filters match the current account, or the panel
    // flashes the old account's history.
    if (!session || session.filters.account !== account?.addr) return []
    const cutoff = Date.now() - RECENT_ACTIVITY_WINDOW_MS
    return session.result.items
      // "Last 24 hours" — keep recent + still-pending (timestamp 0) ops.
      .filter((op) => !op.timestamp || op.timestamp >= cutoff)
      .slice(0, RECENT_ACTIVITY_SHOW)
      .map(accountOpToActivityItem)
      .filter((i): i is NonNullable<typeof i> => !!i)
  }, [isPrivate, accountsOps, sessionId, account?.addr])

  const dashboardNetworkFilterName = useMemo(() => {
    if (!dashboardNetworkFilter) return null
    if (dashboardNetworkFilter === 'rewards') return 'Rewards'
    if (dashboardNetworkFilter === 'gasTank') return 'Gas Tank'
    return (
      allNetworks.find((n) => n.chainId.toString() === dashboardNetworkFilter.toString())?.name ||
      null
    )
  }, [dashboardNetworkFilter, allNetworks])

  const hasRailgunWallet = useMemo(
    () => allRailgunWallets.some((w) => w.accountAddr === account?.addr),
    [allRailgunWallets, account?.addr]
  )

  const privateModeReason = useMemo(() => {
    const base = computePrivateModeReason({ account, keys, hasRailgunWallet })
    if (base !== 'creatable') return base
    if (railgunEngineInitializingError) return 'failed'
    if (railgunEngineInitializing) return 'initializing'
    return base
  }, [account, keys, hasRailgunWallet, railgunEngineInitializing, railgunEngineInitializingError])

  const firstPrivateSupportedChainId = useMemo(() => {
    const set = new Set(EXTENSION_PRIVATE_CHAIN_IDS.map((id) => id.toString()))
    const exists: Record<string, true> = {}
    networks.forEach((n) => {
      exists[n.chainId.toString()] = true
    })
    const balanceTokenMap = currentRailgunWallet?.balanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    const usdByChain =
      balanceTokenMap && prices ? getBalanceUsdByChain(balanceTokenMap, prices) : undefined
    const sorted = networks
      .map((n) => n.chainId.toString())
      .filter((chainId) => set.has(chainId) && exists[chainId])
      .sort((a, b) => (usdByChain?.get(Number(b)) ?? 0) - (usdByChain?.get(Number(a)) ?? 0))
    return sorted.length ? sorted[0] : EXTENSION_PRIVATE_CHAIN_IDS[0]?.toString()
  }, [currentRailgunWallet?.balanceTokenMap, currentRailgunWallet?.balanceTokenPriceMap, networks])

  const handleModeChange = useCallback(
    (nextMode: Mode) => {
      setSelectedTokenId(null)
      const nextFilter = resolveDashboardNetworkFilterForModeChange({
        previousMode: mode,
        nextMode,
        currentFilter: dashboardNetworkFilter,
        isExtension,
        firstPrivateSupportedChainId
      })
      if (nextFilter !== undefined) {
        dispatch({
          type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
          params: { dashboardNetworkFilter: nextFilter }
        })
      }
      if (nextMode === ModeEnum.PRIVATE) {
        setTimeout(() => setMode(nextMode), 50)
      } else {
        setMode(nextMode)
      }
    },
    [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId, setMode]
  )

  // Bounce back to public when private mode isn't usable: either no railgun
  // wallet exists, or the account can never go private (view-only / hardware /
  // raw private-key). The blocked check is what handles a view-only account that
  // still carries a stale railgun wallet row — hasRailgunWallet alone wouldn't.
  useEffect(() => {
    if (mode !== ModeEnum.PRIVATE) return
    if (railgunIsLoading) return
    // Init-on-demand: when the user opts into private mode on a seed account that
    // has no Railgun wallet yet, the engine boots and the wallet is created lazily.
    // 'creatable' (pre-boot) and 'initializing' (booting) are that in-flight window
    // — don't bounce back to public, or the toggle would snap off mid-setup.
    if (privateModeReason === 'creatable' || privateModeReason === 'initializing') return
    if (hasRailgunWallet && !isPrivateModeBlocked(privateModeReason)) return
    handleModeChange(ModeEnum.PUBLIC)
  }, [hasRailgunWallet, privateModeReason, mode, handleModeChange, railgunIsLoading])

  // Keep a valid private-supported chain selected when entering private mode.
  useEffect(() => {
    if (!isExtension || mode !== ModeEnum.PRIVATE || !firstPrivateSupportedChainId) return
    const next = resolveExtensionPrivateInvariantFilter(dashboardNetworkFilter, firstPrivateSupportedChainId)
    if (next !== undefined) {
      dispatch({
        type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
        params: { dashboardNetworkFilter: next }
      })
    }
  }, [mode, dashboardNetworkFilter, dispatch, firstPrivateSupportedChainId])

  // ---- Balances ---------------------------------------------------------
  const publicTotal = portfolio?.totalBalance || 0
  const privateTotal = railgunOverview.totalRailgunBalanceForDisplay || 0
  const displayTotal = isPrivate ? privateTotal : publicTotal
  const balanceFormatted = formatDecimals(displayTotal, 'value')

  // Lazy init-on-demand: when the user clicks Private the engine boots BEFORE any
  // balance fetch starts, so `isRailgunBalanceFetching` is still false during that
  // window. Fold `railgunEngineInitializing` into the private loading flags so the
  // balance skeleton + spinning refresh icon light up the instant the user opts in
  // — one combined "loading private wallet" state instead of a dead gap before the
  // merkletree scan kicks off.
  const isBalanceLoading = isPrivate
    ? (railgunEngineInitializing || railgunOverview.isRailgunBalanceFetching) &&
      !railgunOverview.hasPriorBalance
    : !portfolio?.isReadyToVisualize

  // True whenever the private wallet is booting OR a balance fetch is in flight
  // (initial load OR background refresh). Drives the spinning refresh icon —
  // mirrors the extension's `isFetchingBalance`.
  const isSyncing = isPrivate
    ? railgunEngineInitializing || railgunOverview.isRailgunBalanceFetching
    : !portfolio?.isAllReady || !!portfolio?.isReloading

  // Private merkletree scan % — shown next to the refresh icon while a private
  // balance fetch is in flight (mirrors the extension DashboardOverview).
  const showScanProgress = isPrivate && railgunOverview.isRailgunBalanceFetching

  // ---- Pending private balance (shield processing + missing POI) --------
  // Mirrors the extension DashboardOverview pending row: a "Processing …" /
  // "N processing in Activity" shield badge and the "Incoming $X" pill for
  // received funds still awaiting POI. Hidden while the balance is fetching.
  const shouldShowBatchOrHint = isPrivate && railgunOverview.isRailgunBalanceFetching
  const shieldPending = useRailgunShieldPending(mode)
  const { totalMissingPoiUsd, showPrivatePendingRow } = useDashboardPrivatePendingRow({
    mode,
    shouldShowBatchOrHint,
    isRailgunBalanceFetching: railgunOverview.isRailgunBalanceFetching,
    totalMissingInternalPOIUSD: railgunOverview.totalMissingInternalPOIUSD,
    totalMissingExternalPOIUSD: railgunOverview.totalMissingExternalPOIUSD,
    shieldPending
  })

  // ---- Tokens -----------------------------------------------------------
  // Per-token pending-POI USD (private mode): the slice of the "Incoming $X"
  // pill that belongs to each token, so each row can show a "+$X pending POI"
  // subscript. Mirrors the extension popup's BaseTokenItem.
  const pendingPOIByToken = useMemo<Map<string, number>>(() => {
    if (!isPrivate) return new Map()
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    if (!prices) return new Map()
    return getMissingPOIUsdByToken(
      currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
      currentRailgunWallet?.missingExternalPOIBalanceTokenMap,
      prices
    )
  }, [
    isPrivate,
    currentRailgunWallet?.balanceTokenPriceMap,
    currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
    currentRailgunWallet?.missingExternalPOIBalanceTokenMap
  ])

  // Set of `address-chainId` keys the user has hidden, derived straight from the
  // controller preference (authoritative + synchronous). Lower-cased address and
  // stringified chainId so it survives bigint/string drift across the worker.
  const hiddenPreferenceKeys = useMemo(() => {
    const set = new Set<string>()
    tokenPreferences.forEach((pref) => {
      if (pref.isHidden) set.add(`${pref.address?.toLowerCase()}-${pref.chainId?.toString()}`)
    })
    return set
  }, [tokenPreferences])

  // Set of `address-chainId` keys the user manually added. Same key shape as
  // hiddenPreferenceKeys so the bigint/string drift across the worker is handled.
  const customTokenKeys = useMemo(() => {
    const set = new Set<string>()
    customTokens.forEach((t) => set.add(`${t.address?.toLowerCase()}-${t.chainId?.toString()}`))
    return set
  }, [customTokens])

  const sortedTokens = useMemo<SelectedAccountPortfolioTokenResult[]>(() => {
    const source = (isPrivate ? railgunTokens : portfolio?.tokens || []) as SelectedAccountPortfolioTokenResult[]
    const filtered = [...source]
      .filter((token) => {
        if (token.flags.isHidden) return false
        if (hiddenPreferenceKeys.has(`${token.address?.toLowerCase()}-${token.chainId?.toString()}`))
          return false
        if (token.flags.onGasTank) return false
        if (token.flags.defiTokenType === AssetType.Borrow) return false
        if (dashboardNetworkFilter && dashboardNetworkFilter !== 'rewards' && dashboardNetworkFilter !== 'gasTank') {
          if (token.chainId?.toString() !== dashboardNetworkFilter.toString()) return false
        }
        if (hasAmount(token)) return true
        // Keep user-added (custom) tokens even at a 0 balance — they only leave
        // the dashboard via the explicit "Hide token" action (checked above).
        if (customTokenKeys.has(`${token.address?.toLowerCase()}-${token.chainId?.toString()}`))
          return true
        // Keep pending-only tokens (0 spendable but funds awaiting POI) so the
        // user sees which token the "Incoming $X" pill refers to. useRailgunTokens
        // already adds these as amount-0 rows; without this they're filtered out.
        return (pendingPOIByToken.get(priceKey(Number(token.chainId), token.address)) ?? 0) > 0
      })
      .sort((a, b) => {
        const diff = getTokenBalanceInUSD(b) - getTokenBalanceInUSD(a)
        if (diff !== 0) return diff
        return Number(getTokenAmount(b)) - Number(getTokenAmount(a))
      })

    if (!searchValue) return filtered as SelectedAccountPortfolioTokenResult[]

    return tokenOrCollectionSearch({
      networks: allNetworks,
      assets: filtered as TokenResult[],
      search: searchValue,
      searchType: 'token'
    }) as SelectedAccountPortfolioTokenResult[]
  }, [
    isPrivate,
    railgunTokens,
    portfolio?.tokens,
    dashboardNetworkFilter,
    searchValue,
    allNetworks,
    pendingPOIByToken,
    hiddenPreferenceKeys,
    customTokenKeys
  ])

  const assets = useMemo<AssetRowData[]>(
    () =>
      sortedTokens.map((token) => {
        const details = getAndFormatTokenDetails(token, networks)
        return {
          id: getTokenId(token),
          symbol: token.symbol,
          network: details.networkData?.name || '',
          chainId: token.chainId,
          address: token.address,
          decimals: token.decimals,
          balanceFormatted: details.balanceFormatted,
          balanceUSDFormatted: details.balanceUSDFormatted,
          priceUSDFormatted: details.priceUSDFormatted,
          deltaPct: get24hChange(token),
          pendingPOIUsd: pendingPOIByToken.get(priceKey(Number(token.chainId), token.address)) ?? 0,
          raw: token
        }
      }),
    [sortedTokens, networks, pendingPOIByToken]
  )

  // Only surface the "You don't have any tokens yet." empty state when the
  // headline balance is actually 0. The header balance (`displayTotal`) is
  // wallet-wide and stays > 0 even under a network filter, so an empty token
  // list while it shows e.g. $36.11 is never a true "empty wallet" — it's a
  // data gap (per-token maps are in-memory only and get wiped on cold-load /
  // worker re-init; the donut total persists). In that window show the skeleton
  // instead. Mirrors the common Tokens.tsx cached-balance guard.
  const hasBalanceButNoTokens = displayTotal > 0 && assets.length === 0
  const isTokensLoading = isPrivate
    ? (railgunOverview.isRailgunBalanceFetching || hasBalanceButNoTokens) && assets.length === 0
    : (!portfolio?.isAllReady || hasBalanceButNoTokens) && assets.length === 0

  // Private cold-sync gap: the merkletree scan finishes (isRailgunBalanceFetching
  // flips false) but POI verification keeps running in the scan worker, so
  // received funds are pending-POI and not yet in the spendable token list.
  // Without this the token area shows a false "You don't have any tokens yet."
  // while "Incoming $X" is on the balance card. Surface a "verifying" hint
  // instead. Gated on the same pending signals that drive the incoming pill.
  const isVerifyingPrivateBalance =
    isPrivate &&
    assets.length === 0 &&
    (showPrivatePendingRow || !!shieldPending?.showBadge || railgunOverview.isRailgunBalanceFetching)

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === selectedTokenId) || null,
    [assets, selectedTokenId]
  )

  // ---- Hide token -------------------------------------------------------
  // Load the persisted "Don't ask me again" preference once (shared storage key
  // with the extension token-details flow).
  useEffect(() => {
    storage
      .get('doNotShowAgainModalHideToken', false)
      .then(setDoNotAskHideToken)
      .catch(() => console.error('Failed to load doNotShowAgainModalHideToken'))
  }, [])

  // Dispatch the actual hide and dismiss the detail panel. Mirrors the
  // extension's PortfolioController.toggleHideToken(token, accountAddr, true).
  const hideSelectedToken = useCallback(() => {
    if (!selectedAsset) return
    dispatch({
      type: 'method',
      params: {
        ctrlName: 'PortfolioController',
        method: 'toggleHideToken',
        args: [
          { address: selectedAsset.address, chainId: selectedAsset.chainId },
          account?.addr,
          true
        ]
      }
    } as any)
    setSelectedTokenId(null)
  }, [selectedAsset, account?.addr, dispatch])

  // Hide button on the detail panel: hide immediately when the user opted out of
  // the confirmation, otherwise open the confirm popup.
  const requestHideSelectedToken = useCallback(() => {
    if (doNotAskHideToken) hideSelectedToken()
    else setHideTokenModalVisible(true)
  }, [doNotAskHideToken, hideSelectedToken])

  const confirmHideToken = useCallback(
    (doNotAskAgain: boolean) => {
      if (doNotAskAgain) {
        setDoNotAskHideToken(true)
        storage
          .set('doNotShowAgainModalHideToken', true)
          .catch(() => console.error('Failed to persist doNotShowAgainModalHideToken'))
      }
      setHideTokenModalVisible(false)
      hideSelectedToken()
    },
    [hideSelectedToken]
  )

  const cancelHideToken = useCallback(() => setHideTokenModalVisible(false), [])

  // ---- Account identity -------------------------------------------------
  const accountName = account?.preferences?.label || (account?.addr ? shortenAddress(account.addr, 13) : '')
  const accountAddress = account?.addr || ''
  // In private mode the card shows the Railgun (0zk…) address, not the public 0x one.
  const displayAddress =
    isPrivate && currentRailgunWallet?.railgunAddress
      ? currentRailgunWallet.railgunAddress
      : accountAddress
  const accountPfp = account?.preferences?.pfp || account?.addr || ''
  const accountIsSmart = useMemo(() => (account ? isSmartAccount(account) : false), [account])

  // ---- Actions / navigation --------------------------------------------
  const refresh = useCallback(() => {
    if (isPrivate) {
      void railgunOverview.fetchRailgunBalance()
    } else {
      dispatch({
        type: 'MAIN_CONTROLLER_RELOAD_SELECTED_ACCOUNT',
        params: { chainId: dashboardNetworkFilter ?? undefined }
      })
    }
  }, [isPrivate, railgunOverview, dispatch, dashboardNetworkFilter])

  const goTo = useCallback((route: string) => navigate(route), [navigate])
  // Tapping the account avatar opens the account-select screen, mirroring the
  // extension's DashboardHeader AccountButton behavior.
  const goToAccountSelect = useCallback(() => navigate(WEB_ROUTES.accountSelect), [navigate])

  return {
    // mode
    mode,
    isPrivate,
    handleModeChange,
    privateModeReason,
    ensureRailgunEngine,
    railgunEngineInitializing,
    // identity
    accountName,
    accountAddress,
    displayAddress,
    accountPfp,
    accountIsSmart,
    goToAccountSelect,
    // balance
    balanceFormatted,
    isBalanceLoading,
    isSyncing,
    showScanProgress,
    scanningProgress,
    // "Initial load may take up to 15 minutes" hint — shown inside the balance
    // card after 15s of a first-time private balance fetch (mirrors the extension
    // DashboardOverview BatchOrFirstBalanceHint).
    showFirstBalanceLoadHint: railgunOverview.showFirstBalanceLoadHint,
    // pending private balance row (shield processing badge + missing-POI pill)
    shieldPending,
    totalMissingPoiUsd,
    showPrivatePendingRow,
    hidden,
    toggleHidden: () => setHidden((h) => !h),
    refresh,
    // tokens
    assets,
    isTokensLoading,
    isVerifyingPrivateBalance,
    // private-balance fetch failed (e.g. Railgun engine/IndexedDB dead after
    // incognito sleep) — surface a retry instead of a false "no tokens" state.
    privateBalanceError: isPrivate && isBalanceFetchingError,
    selectedAsset,
    selectedTokenId,
    selectToken: (id: string) => setSelectedTokenId((cur) => (cur === id ? null : id)),
    clearSelectedToken: () => setSelectedTokenId(null),
    // hide-token confirm popup
    hideTokenModalVisible,
    requestHideSelectedToken,
    confirmHideToken,
    cancelHideToken,
    // toolbar: network filter + asset-type tabs + search (previous-version flow)
    openTab,
    setOpenTab,
    sessionId,
    initTab,
    animatedOverviewHeight,
    allNetworks,
    dashboardNetworkFilter,
    dashboardNetworkFilterName,
    searchControl,
    clearSearch: () => setValue('search', ''),
    // recent activity panel (public)
    recentActivity,
    // totals for the privacy donut
    publicTotal,
    privateTotal,
    // navigation
    goTo,
    routes: WEB_ROUTES
  }
}

export default useHomeDashboard
